import React, { useState, useEffect } from 'react';
import { 
  X, Briefcase, FileText, Activity, Users, Building, MapPin, Calendar, 
  DollarSign, Clock, User, Award, Target, Settings, Edit, MessageSquare
} from 'lucide-react';
import NotesTab from '../shared/NotesTab';
import ActivityTab from '../shared/ActivityTab';
import { Dialog, DialogContent } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const JobDetailModal = ({ job, isOpen, onClose, submissions = [], onEdit, canEdit = false }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [jobSubmissions, setJobSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Get current user
  React.useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }
  }, []);

  useEffect(() => {
    if (job && isOpen) {
      // Filter submissions for this job
      const filtered = submissions.filter(s => s.job_id === job.id);
      setJobSubmissions(filtered);
    }
  }, [job, submissions, isOpen]);

  if (!job) return null;

  // Handle edit button click
  const handleEditClick = () => {
    if (onEdit) {
      onEdit(job);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      'accepting_candidates': 'bg-green-100 text-green-800',
      'covered': 'bg-blue-100 text-blue-800',
      'interviewing': 'bg-yellow-100 text-yellow-800',
      'offer_out': 'bg-purple-100 text-purple-800',
      'filled': 'bg-emerald-100 text-emerald-800',
      'on_hold': 'bg-gray-100 text-gray-800',
      'cancelled': 'bg-red-100 text-red-800',
      'lost': 'bg-orange-100 text-orange-800',
      'declined': 'bg-pink-100 text-pink-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    const colors = {
      'high': 'bg-red-100 text-red-800',
      'normal': 'bg-blue-100 text-blue-800',
      'low': 'bg-gray-100 text-gray-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get submission stats
  const submissionStats = {
    total: jobSubmissions.length,
    clientSubmission: jobSubmissions.filter(s => s.current_status === 'Client Submission').length,
    interview: jobSubmissions.filter(s => s.current_status === 'Interview').length,
    offerExtended: jobSubmissions.filter(s => s.current_status === 'Offer Extended').length,
    onboarded: jobSubmissions.filter(s => s.current_status === 'Onboarded').length
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="job-detail-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Briefcase className="h-6 w-6 text-blue-600" />
              <h2 
                className="text-2xl font-semibold text-foreground"
                style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}
                data-testid="job-title-header"
              >
                {job.title || 'Untitled Job'}
              </h2>
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              {job.client_company && (
                <div className="flex items-center gap-1">
                  <Building className="h-4 w-4" />
                  <span className="font-medium">{job.client_company}</span>
                </div>
              )}
              {job.location && (
                <>
                  <span>•</span>
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    <span>{job.location}</span>
                  </div>
                </>
              )}
              {job.employment_type && (
                <>
                  <span>•</span>
                  <span className="capitalize">{job.employment_type.replace('_', ' ')}</span>
                </>
              )}
            </div>
          </div>
          
          {/* Status and Priority Badges */}
          <div className="flex gap-2 ml-4">
            <Badge 
              className={getStatusColor(job.status)}
              data-testid="job-status-badge"
            >
              {job.status?.replace('_', ' ').toUpperCase()}
            </Badge>
            <Badge 
              className={getPriorityColor(job.priority)}
              data-testid="job-priority-badge"
            >
              {job.priority?.toUpperCase()}
            </Badge>
          </div>
        </div>

        <Separator className="mb-4" />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <TabsList data-testid="job-detail-tabs">
              <TabsTrigger value="details" className="gap-2" data-testid="tab-details">
                <FileText className="h-4 w-4" />
                Details
              </TabsTrigger>
              <TabsTrigger value="requirements" className="gap-2" data-testid="tab-requirements">
                <Target className="h-4 w-4" />
                Requirements
              </TabsTrigger>
              <TabsTrigger value="submissions" className="gap-2" data-testid="tab-submissions">
                <Users className="h-4 w-4" />
                Submissions ({submissionStats.total})
              </TabsTrigger>
              <TabsTrigger value="activity" className="gap-2" data-testid="tab-activity">
                <Activity className="h-4 w-4" />
                Activity
              </TabsTrigger>
              <TabsTrigger value="notes" className="gap-2" data-testid="tab-notes">
                <MessageSquare className="h-4 w-4" />
                Notes
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Details Tab */}
            <TabsContent value="details" className="space-y-6 mt-0" data-testid="details-content">
              {/* Basic Information */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground/90" style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}>
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-2 text-sm">
                    <Building className="h-4 w-4 text-primary mt-0.5" />
                    <div>
                      <span className="text-muted-foreground">Client:</span>
                      <span className="ml-1 font-medium">{job.client_company || job.client || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-primary mt-0.5" />
                    <div>
                      <span className="text-muted-foreground">Date Opened:</span>
                      <span className="ml-1 font-medium">{formatDate(job.date_job_opened)}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <User className="h-4 w-4 text-primary mt-0.5" />
                    <div>
                      <span className="text-muted-foreground">Openings:</span>
                      <span className="ml-1 font-medium">{job.no_of_openings || 1}</span>
                    </div>
                  </div>
                  {job.department && (
                    <div className="flex items-start gap-2 text-sm">
                      <Settings className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Department:</span>
                        <span className="ml-1 font-medium">{job.department}</span>
                      </div>
                    </div>
                  )}
                  {job.hiring_manager && (
                    <div className="flex items-start gap-2 text-sm">
                      <User className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Hiring Manager:</span>
                        <span className="ml-1 font-medium">{job.hiring_manager}</span>
                      </div>
                    </div>
                  )}
                  {job.account_manager && (
                    <div className="flex items-start gap-2 text-sm">
                      <User className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Account Manager:</span>
                        <span className="ml-1 font-medium">{job.account_manager}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Job Description */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground/90" style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}>
                  Job Description
                </h3>
                <div 
                  className="prose prose-sm max-w-none text-muted-foreground bg-muted/50 rounded-lg p-4"
                  dangerouslySetInnerHTML={{ __html: job.description || 'No description available' }}
                  data-testid="job-description"
                />
              </div>

              <Separator />

              {/* Work Arrangement */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground/90" style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}>
                  Work Arrangement
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {job.location_requirements && (
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Work Mode:</span>
                        <span className="ml-1 font-medium capitalize">{job.location_requirements}</span>
                      </div>
                    </div>
                  )}
                  {job.travel && (
                    <div className="flex items-start gap-2 text-sm">
                      <Clock className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Travel:</span>
                        <span className="ml-1 font-medium">{job.travel}</span>
                      </div>
                    </div>
                  )}
                  {job.visa_sponsorship && (
                    <div className="flex items-start gap-2 text-sm">
                      <Award className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Visa Sponsorship:</span>
                        <span className="ml-1 font-medium">{job.visa_sponsorship}</span>
                      </div>
                    </div>
                  )}
                  {job.relocation_allowed && (
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Relocation:</span>
                        <span className="ml-1 font-medium">{job.relocation_allowed}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Compensation */}
              {(job.bill_rate || job.salary_range) && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground/90" style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}>
                      Compensation
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {job.bill_rate && (
                        <div className="flex items-start gap-2 text-sm">
                          <DollarSign className="h-4 w-4 text-primary mt-0.5" />
                          <div>
                            <span className="text-muted-foreground">Bill Rate:</span>
                            <span className="ml-1 font-medium">${job.bill_rate}/{job.pay_unit || 'hour'}</span>
                          </div>
                        </div>
                      )}
                      {job.salary_range && job.salary_range.min && (
                        <div className="flex items-start gap-2 text-sm">
                          <DollarSign className="h-4 w-4 text-primary mt-0.5" />
                          <div>
                            <span className="text-muted-foreground">Salary Range:</span>
                            <span className="ml-1 font-medium">
                              ${job.salary_range.min?.toLocaleString()} - ${job.salary_range.max?.toLocaleString()} {job.salary_range.currency || 'USD'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Requirements Tab */}
            <TabsContent value="requirements" className="space-y-6 mt-0" data-testid="requirements-content">
              {/* Required Skills */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground/90" style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}>
                  Required Skills
                </h3>
                {job.required_skills && job.required_skills.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {job.required_skills.map((skill, index) => (
                      <Badge key={index} variant="secondary" className="bg-blue-100 text-blue-800">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No required skills specified</p>
                )}
              </div>

              <Separator />

              {/* Preferred Skills */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground/90" style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}>
                  Preferred Skills
                </h3>
                {job.preferred_skills && job.preferred_skills.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {job.preferred_skills.map((skill, index) => (
                      <Badge key={index} variant="outline" className="border-green-200 text-green-700">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No preferred skills specified</p>
                )}
              </div>

              <Separator />

              {/* Experience Level */}
              {job.experience_level && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-foreground/90" style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}>
                    Experience Level
                  </h3>
                  <Badge className="bg-purple-100 text-purple-800 capitalize">
                    {job.experience_level.replace('_', ' ')}
                  </Badge>
                </div>
              )}
            </TabsContent>

            {/* Submissions Tab */}
            <TabsContent value="submissions" className="space-y-4 mt-0" data-testid="submissions-content">
              {/* Submission Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <p className="text-sm text-muted-foreground">Total</p>
                  <p className="text-2xl font-bold text-blue-600">{submissionStats.total}</p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                  <p className="text-sm text-muted-foreground">Client Sub</p>
                  <p className="text-2xl font-bold text-indigo-600">{submissionStats.clientSubmission}</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <p className="text-sm text-muted-foreground">Interview</p>
                  <p className="text-2xl font-bold text-yellow-600">{submissionStats.interview}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <p className="text-sm text-muted-foreground">Offer</p>
                  <p className="text-2xl font-bold text-purple-600">{submissionStats.offerExtended}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <p className="text-sm text-muted-foreground">Onboarded</p>
                  <p className="text-2xl font-bold text-green-600">{submissionStats.onboarded}</p>
                </div>
              </div>

              <Separator />

              {/* Submissions List */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground/90" style={{ fontFamily: "'Brockmann', 'Figtree', ui-sans-serif" }}>
                  Recent Submissions
                </h3>
                {jobSubmissions.length > 0 ? (
                  <div className="space-y-2">
                    {jobSubmissions.slice(0, 10).map((submission, index) => (
                      <div 
                        key={submission.id || index} 
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-sm">{submission.candidate_name || 'Unknown Candidate'}</p>
                          <p className="text-xs text-muted-foreground">
                            Submitted by {submission.recruiter_name || 'Unknown'} • {formatDate(submission.submission_date)}
                          </p>
                        </div>
                        <Badge className={`ml-4 ${
                          submission.current_status === 'Onboarded' ? 'bg-green-100 text-green-800' :
                          submission.current_status === 'Client Submission' ? 'bg-indigo-100 text-indigo-800' :
                          submission.current_status === 'Interview' ? 'bg-yellow-100 text-yellow-800' :
                          submission.current_status === 'Offer Extended' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {submission.current_status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No submissions yet</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Activity Tab */}
            <TabsContent value="activity" className="space-y-4 mt-0" data-testid="activity-content">
              <ActivityTab 
                entityType="job" 
                entityId={job?.id} 
              />
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="space-y-4 mt-0" data-testid="notes-content">
              <NotesTab 
                entityType="job" 
                entityId={job?.id}
                currentUser={currentUser}
              />
            </TabsContent>
          </div>
        </Tabs>
        
        {/* Footer */}
        <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-border">
          <Button variant="outline" onClick={onClose} data-testid="close-modal-btn">
            Close
          </Button>
          {onEdit && canEdit && (
            <Button variant="default" onClick={() => onEdit(job)} data-testid="edit-job-btn">
              Edit Job
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default JobDetailModal;
